import * as THREE from "three";

import type { PlanePool } from "./PlanePool";
import { findSiblingVideo, OriginalMedia } from "./WorkListing";

const HOVER_INTENT_MS = 250;

const hoverActiveClass = "is-hover-active";
const mirrorActiveClass = "is-mirror-active";
const listingActiveClass = "is-listing-active";

export function initMusicWorkListing(planePool: PlanePool, root: HTMLElement): () => void {
    const cardRoots = root.querySelectorAll<HTMLElement>(".work-card");
    if (!cardRoots.length) return () => {};

    const listing = root.querySelector<HTMLElement>(".listing__list");

    const originals = new WeakMap<THREE.ShaderMaterial, OriginalMedia>();
    const videoTextures = new Map<HTMLVideoElement, THREE.VideoTexture>();
    const cleanups: Array<() => void> = [];
    const resets: Array<() => void> = [];
    let navigating = false;
    let hoverIntentId: number | undefined;

    function cancelHoverIntent() {
        if (hoverIntentId !== undefined) {
            window.clearTimeout(hoverIntentId);
            hoverIntentId = undefined;
        }
    }

    function getVideoTexture(video: HTMLVideoElement): THREE.VideoTexture {
        let tex = videoTextures.get(video);
        if (!tex) {
            tex = new THREE.VideoTexture(video);
            tex.minFilter = THREE.LinearFilter;
            tex.magFilter = THREE.LinearFilter;
            videoTextures.set(video, tex);
        }
        return tex;
    }

    function clearState(card: HTMLElement) {
        card.classList.remove(hoverActiveClass, mirrorActiveClass);
    }

    function setHoverState(card: HTMLElement) {
        card.classList.add(hoverActiveClass);
        card.classList.remove(mirrorActiveClass);
    }

    function setMirrorState(card: HTMLElement) {
        card.classList.add(mirrorActiveClass);
        card.classList.remove(hoverActiveClass);
    }

    cardRoots.forEach(cardRoot => {
        const el = cardRoot.querySelector<HTMLElement>("[data-webgl]");
        if (!el) return;
        const video = findSiblingVideo(el);
        if (!video) return;

        if (video && video.crossOrigin !== "anonymous") {
            video.crossOrigin = "anonymous";
        }

        const startHover = () => {
            const plane = planePool.getPlane(el);
            if (!plane) return;
            const mat = plane.mesh.material as THREE.ShaderMaterial;

            if (!originals.has(mat)) {
                originals.set(mat, {
                    texture: mat.uniforms.uTexture.value,
                    aspect: mat.uniforms.uImageAspect.value,
                });
            }

            void video.play().catch(() => {});

            listing?.classList.add(listingActiveClass);
            setHoverState(cardRoot);
            setMirrorState(cardRoot);

            const tex = getVideoTexture(video);

            const swapIn = () => {
                if (!cardRoot.classList.contains(mirrorActiveClass)) return;
                const plane = planePool.getPlane(el);
                const mat = plane?.mesh.material as THREE.ShaderMaterial | undefined;
                if (!mat) return;
                mat.uniforms.uTexture.value = tex;
                mat.uniforms.uImageAspect.value = video.videoWidth / video.videoHeight || mat.uniforms.uImageAspect.value;
                mat.uniforms.uMirrorEnabled.value = 1;
            };

            if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                swapIn();
            } else {
                video.addEventListener("loadeddata", swapIn, { once: true });
            }
        };

        const onEnter = () => {
            cancelHoverIntent();
            hoverIntentId = window.setTimeout(() => {
                hoverIntentId = undefined;
                startHover();
            }, HOVER_INTENT_MS);
        };

        const onLeave = () => {
            cancelHoverIntent();
            if (navigating) return;

            const plane = planePool.getPlane(el);
            if (!plane) return;
            const mat = plane.mesh.material as THREE.ShaderMaterial;
            const original = originals.get(mat);

            video.pause();
            video.currentTime = 0;
            mat.uniforms.uMirrorEnabled.value = 0;

            clearState(cardRoot);
            listing?.classList.remove(listingActiveClass);

            if (original) {
                mat.uniforms.uTexture.value = original.texture;
                mat.uniforms.uImageAspect.value = original.aspect;
            }
        };

        cardRoot.addEventListener("mouseenter", onEnter);
        cardRoot.addEventListener("mouseleave", onLeave);

        resets.push(() => {
            video.pause();
            video.currentTime = 0;
            onLeave();
        });

        cleanups.push(() => {
            cardRoot.removeEventListener("mouseenter", onEnter);
            cardRoot.removeEventListener("mouseleave", onLeave);
        });
    });

    const onNavStart = () => {
        navigating = true;
        cancelHoverIntent();
    };

    const onNavEnd = () => {
        navigating = false;
        resets.forEach(fn => fn());
    };
    window.addEventListener("barba:nav-start", onNavStart);
    window.addEventListener("barba:nav-end", onNavEnd);

    return function destroy() {
        window.removeEventListener("barba:nav-start", onNavStart);
        window.removeEventListener("barba:nav-end", onNavEnd);
        cleanups.forEach(fn => fn());
        cancelHoverIntent();
        resets.forEach(fn => fn());
        videoTextures.forEach(tex => tex.dispose());
        videoTextures.clear();
    };
}
